import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { clubs as mockClubs, reviews as mockReviews, news as mockNews, events as mockEvents } from '../data/mockData';
import api from '../api/client';
import { useAuth, useData } from '../context';
import { AdBox, WysiwygEditor, RichDisplay, countryDisplay, CountrySelect } from '../components/Shared';
import Turnstile, { TURNSTILE_SITE_KEY } from '../components/Turnstile';

// ── Sidebar helpers ──────────────────────────────────────
const CD_MONTH_LT    = ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'];
const CD_MONTH_SHORT = ['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'];
const CD_EV_COLORS   = { fest:'#ffa040', larp:'#ff8080', ccg:'#80b0ff', rpg:'#80d060', event:'#a080ff' };
const CD_TYPE_BG     = { club:'bg-club', larp:'bg-larp', ccg:'bg-ccg', rpg:'bg-rpg', fest:'bg-fest' };

function buildCdCal(year, month) {
  const first = new Date(year, month, 1).getDay(); // 0=Sun
  const offset = (first + 6) % 7; // Mon-based
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const normClub = c => ({
  ...c,
  youtube: c.youtube_url ?? c.youtube ?? null,
  members: c.members ?? 0,
  rating: c.rating ?? 0,
  tags: Array.isArray(c.tags) ? c.tags : [],
  projects: Array.isArray(c.projects) ? c.projects : [],
  owner_id: c.owner_id ?? null,
  image_url: c.image_url || '',
  gallery: (() => {
    try { return JSON.parse(c.gallery || '[]'); } catch { return []; }
  })(),
});

const normReview = r => ({
  id: r.id,
  clubId: r.entity_id ?? r.clubId,
  user: r.user_name ?? r.user ?? 'Vartotojas',
  rating: r.rating,
  comment: r.comment,
  date: r.created_at ? r.created_at.split('T')[0] : (r.date || ''),
  helpful: r.helpful_count ?? r.helpful ?? 0,
});

// ── Rich text renderer — wraps shared RichDisplay with club-richtext class ──
function RichText({ text, className }) {
  return <RichDisplay html={text} className={`club-richtext${className ? ' ' + className : ''}`} />;
}

// ── Login prompt overlay ─────────────────────────────────
function LoginPrompt({ onClose }) {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '36px 40px', maxWidth: 380, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 20, color: 'var(--text)', marginBottom: 8 }}>{t('club.login_required')}</h3>
        <p style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>{t('club.login_prompt_edit')}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={onClose}>{t('nav.login')}</button>
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm delete modal ─────────────────────────────────
function ConfirmDelete({ name, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onCancel}>
      <div style={{ background: 'var(--bg)', border: '1px solid #c0392b', borderRadius: 'var(--radius-lg)', padding: '32px 36px', maxWidth: 360, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 18, color: 'var(--text)', marginBottom: 8 }}>{t('club.delete_title')}</h3>
        <p style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 24 }}>
          {t('club.delete_confirm')} <strong style={{ color: 'var(--text)' }}>{name}</strong>?<br />{t('club.delete_irreversible')}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn" style={{ background: '#c0392b', color: '#fff', border: 'none' }} onClick={onConfirm}>🗑️ {t('club.delete_btn')}</button>
          <button className="btn btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin assign panel ───────────────────────────────────
function AdminAssignPanel({ clubId, currentOwnerId }) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState(currentOwnerId || '');
  const [msg, setMsg] = useState('');
  const assign = () => {
    api.patch(`/admin/clubs/${clubId}/assign?user_id=${userId}`)
      .then(() => setMsg('✅ ' + t('club.assign_success')))
      .catch(e => setMsg('❌ ' + (e.response?.data?.detail || t('common.error'))));
  };
  return (
    <div style={{ background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.3)', borderRadius: 'var(--radius)', padding: 16, marginTop: 16 }}>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: '#ffa040', marginBottom: 10 }}>🛡️ {t('club.admin_assign')}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="form-input" style={{ flex: 1 }} type="number" placeholder={t('club.user_id')} value={userId} onChange={e => setUserId(e.target.value)} />
        <button className="btn btn-sm" style={{ background: '#ffa040', color: '#1a1a1a', border: 'none', fontWeight: 700 }} onClick={assign}>{t('club.assign')}</button>
      </div>
      {msg && <div style={{ fontSize: 12, marginTop: 8, color: msg.startsWith('✅') ? '#60d090' : '#ff6060' }}>{msg}</div>}
      {currentOwnerId && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{t('club.current_owner_id')} {currentOwnerId}</div>}
    </div>
  );
}

// ── Image upload helper ──────────────────────────────────
async function uploadFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data.url;
}

// ── Gallery tab ──────────────────────────────────────────
function GalleryTab({ club, canEdit, onGalleryChange, onCoverChange }) {
  const { t } = useTranslation();
  const [gallery, setGallery] = useState(club.gallery || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const update = (next) => { setGallery(next); setDirty(true); };

  const handleFiles = async (files) => {
    if (!canEdit) return;
    setUploading(true);
    try {
      const urls = await Promise.all(Array.from(files).map(uploadFile));
      const newItems = urls.map(url => ({ url, is18: false }));
      update([...gallery, ...newItems]);
    } catch { /* silent */ }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  const toggle18 = (i) => {
    const next = gallery.map((img, idx) => idx === i ? { ...img, is18: !img.is18 } : img);
    update(next);
  };

  const setCover = (url) => {
    onCoverChange(url);
  };

  const remove = (i) => {
    update(gallery.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clubs/${club.id}`, {
        name: club.name, emoji: club.emoji, type: club.type, city: club.city,
        description: club.description, rules: club.rules,
        youtube_url: club.youtube || '', founded: club.founded,
        image_url: club.image_url || '',
        gallery: JSON.stringify(gallery),
      });
      setDirty(false);
      onGalleryChange(gallery);
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div>
      <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 16 }}>🖼️ {t('clubs.gallery')}</h3>

      {dirty && (
        <div className="club-gallery-savebar">
          <span>Nepasaugoti pakeitimai</span>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} style={{ marginLeft: 'auto' }}>
            {saving ? '⏳...' : '💾 Išsaugoti'}
          </button>
        </div>
      )}

      {canEdit && (
        <>
          <div
            className={`club-gallery-drop${dragOver ? ' drag-over' : ''}`}
            onClick={() => fileRef.current.click()}
            onDrop={onDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
          >
            <div className="club-gallery-drop-icon">{uploading ? '⏳' : '📸'}</div>
            <div>{uploading ? 'Įkeliama...' : 'Vilkite nuotraukas čia arba spustelėkite, kad pasirinktumėte'}</div>
            <div style={{ fontSize: 11, marginTop: 6, opacity: .5 }}>PNG, JPG, WEBP • maks. 5MB</div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
        </>
      )}

      {gallery.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(200,180,255,0.3)', fontSize: 14 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🖼️</div>
          Galerija tuščia
        </div>
      ) : (
        <div className="club-gallery-grid">
          {gallery.map((img, i) => (
            <div key={i} className="club-gallery-item">
              <img src={img.url} alt="" className={img.is18 ? 'blurred' : ''} />
              {img.is18 && <div className="club-gallery-age-badge">18+</div>}
              {img.url === club.image_url && <div className="club-gallery-cover-badge">VIRŠELIS</div>}
              <div className="club-gallery-item-overlay">
                {canEdit && (
                  <>
                    <button className={`club-gallery-btn${img.url === club.image_url ? ' active' : ''}`}
                      onClick={() => setCover(img.url)} title="Nustatyti kaip viršelį">🖼</button>
                    <button className={`club-gallery-btn${img.is18 ? ' active' : ''}`}
                      onClick={() => toggle18(i)} title="18+ turinys">18+</button>
                    <button className="club-gallery-btn danger" onClick={() => remove(i)} title="Ištrinti">✕</button>
                  </>
                )}
                {img.is18 && (
                  <button className="club-gallery-btn" style={{ marginLeft: 'auto' }}
                    onClick={() => toggle18(i)}>👁 Rodyti</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Edit form ────────────────────────────────────────────
const EDIT_TABS = ['info', 'description', 'rules'];

function EditForm({ club, onSave, onCancel }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('info');
  const [form, setForm] = useState({
    name: club.name || '',
    emoji: club.emoji || '🛡️',
    type: club.type || 'club',
    city: club.city || '',
    country: club.country || '',
    founded: club.founded || '',
    description: club.description || '',
    rules: club.rules || '',
    youtube_url: club.youtube || '',
    image_url: club.image_url || '',
    gallery: JSON.stringify(club.gallery || []),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCoverFile = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try { set('image_url', await uploadFile(files[0])); } catch { setError('Nepavyko įkelti nuotraukos'); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name.trim()) { setError(t('club.name_required')); return; }
    setSaving(true); setError('');
    try {
      await api.put(`/clubs/${club.id}`, form);
      onSave({ ...club, ...form, youtube: form.youtube_url });
    } catch (e) {
      setError(e.response?.data?.detail || t('club.save_error'));
    } finally { setSaving(false); }
  };

  const TAB_LABELS = { info: '⚙️ Informacija', description: '📝 Aprašymas', rules: '📋 Taisyklės' };

  return (
    <div className="edit-modal-backdrop" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="edit-modal-box">
        {/* Header */}
        <div className="edit-modal-header">
          <span style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: 'var(--gold2)' }}>✏️ {t('club.edit_title')}</span>
          <button style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1 }} onClick={onCancel}>×</button>
        </div>

        {/* Tabs */}
        <div className="edit-modal-tabs">
          {EDIT_TABS.map(id => (
            <button key={id} className={`edit-modal-tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
              {TAB_LABELS[id]}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="edit-modal-body">

          {/* ── Info tab ── */}
          {tab === 'info' && (
            <>
              {/* Cover image */}
              <div style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>
                  Viršelio nuotrauka <span style={{ opacity: .5, fontWeight: 400 }}>(rekomenduojama: 1980×280px)</span>
                </label>
                <div
                  style={{ border: `2px dashed ${dragOver ? 'rgba(240,192,64,0.6)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8, padding: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, transition: 'border-color .2s', background: dragOver ? 'rgba(240,192,64,0.04)' : 'transparent' }}
                  onClick={() => coverRef.current.click()}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleCoverFile(e.dataTransfer.files); }}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                >
                  {form.image_url
                    ? <img src={form.image_url} alt="" style={{ height: 60, width: 120, objectFit: 'cover', borderRadius: 4 }} />
                    : <div style={{ width: 120, height: 60, background: 'rgba(255,255,255,0.05)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🖼️</div>
                  }
                  <div style={{ color: 'rgba(200,180,255,0.5)', fontSize: 12 }}>
                    {uploading ? '⏳ Įkeliama...' : 'Vilkite arba spustelėkite norėdami įkelti viršelį'}
                  </div>
                  {form.image_url && (
                    <button style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(192,57,43,0.4)', color: '#ff8080', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11 }}
                      onClick={e => { e.stopPropagation(); set('image_url', ''); }}>✕ Pašalinti</button>
                  )}
                </div>
                <input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { handleCoverFile(e.target.files); e.target.value = ''; }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">{t('club.field_name')}</label>
                  <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('club.field_emoji')}</label>
                  <input className="form-input" value={form.emoji} onChange={e => set('emoji', e.target.value)} style={{ fontSize: 22 }} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('club.field_type')}</label>
                  <select className="form-input" value={form.type} onChange={e => set('type', e.target.value)}>
                    {['club','larp','rpg','ccg','fest'].map(v => <option key={v} value={v}>{v.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('club.field_city')}</label>
                  <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('clubs.country')}</label>
                  <CountrySelect value={form.country} onChange={v => set('country', v)} className="form-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('club.field_founded')}</label>
                  <input className="form-input" value={form.founded} onChange={e => set('founded', e.target.value)} placeholder="2020" />
                </div>
                <div className="form-group">
                  <label className="form-label">YouTube URL</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="form-input" value={form.youtube_url} onChange={e => set('youtube_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                    {form.youtube_url && (
                      <button className="btn btn-sm" style={{ background: '#c0392b', color: '#fff', border: 'none', flexShrink: 0 }} onClick={() => set('youtube_url', '')}>✕</button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Description tab ── */}
          {tab === 'description' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
                Naudokite formatavimą: paryškintas, kursyvas, sąrašai, antraštės.
              </div>
              <WysiwygEditor
                value={form.description}
                onChange={v => set('description', v)}
                placeholder="Klubo aprašymas..."
                minHeight={320}
              />
            </div>
          )}

          {/* ── Rules tab ── */}
          {tab === 'rules' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
                Dalyvavimo taisyklės, elgesio kodeksas, reikalavimai.
              </div>
              <WysiwygEditor
                value={form.rules}
                onChange={v => set('rules', v)}
                placeholder="Klubo taisyklės..."
                minHeight={320}
              />
            </div>
          )}

          {error && <div style={{ color: '#ff6060', fontSize: 13, marginTop: 14 }}>⚠️ {error}</div>}
        </div>

        {/* Footer */}
        <div className="edit-modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? `⏳ ${t('club.saving')}` : `💾 ${t('common.save')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Contact modal ────────────────────────────────────────
function ContactModal({ club, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [captcha, setCaptcha] = useState('');
  const captchaResetRef = useRef(null);
  const captchaOk = !TURNSTILE_SITE_KEY || !!captcha;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const send = async () => {
    if (!form.subject.trim() || !form.message.trim() || !captchaOk) return;
    setSending(true); setError('');
    try { await api.post(`/clubs/${club.id}/contact`, { ...form, turnstile_token: captcha }); setSent(true); }
    catch (e) { setError(e.response?.data?.detail || t('club.contact_error')); captchaResetRef.current?.(); }
    finally { setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 480, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: 'linear-gradient(135deg,var(--purple),var(--purple2))', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: '#f0c040', fontWeight: 600 }}>📨 {t('club.contact_modal_title')}</div>
            <div style={{ fontSize: 12, color: 'rgba(200,180,255,0.75)', marginTop: 2 }}>{club.emoji} {club.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6 }}>{t('club.contact_sent')}</p>
              <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={onClose}>{t('common.close')}</button>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">{t('club.contact_subject')}</label>
                <input className="form-input" placeholder={t('club.contact_subject_placeholder')} value={form.subject} onChange={e => set('subject', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('club.contact_message')}</label>
                <textarea className="form-textarea" rows={5} placeholder={t('club.contact_message_placeholder')} value={form.message} onChange={e => set('message', e.target.value)} />
              </div>
              {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 12 }}>⚠️ {error}</div>}
              <Turnstile onToken={setCaptcha} resetRef={captchaResetRef} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={send} disabled={sending || !form.subject.trim() || !form.message.trim() || !captchaOk}>
                  {sending ? '⏳...' : `📤 ${t('club.contact_send')}`}
                </button>
                <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Join request modal ───────────────────────────────────
function JoinRequestModal({ club, onClose, onJoined }) {
  const { t } = useTranslation();
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setSending(true); setError('');
    try {
      await api.post(`/clubs/${club.id}/join`, { message: msg });
      setSent(true);
      onJoined('pending');
    } catch (e) {
      setError(e.response?.data?.detail || t('clubs.join_error'));
    } finally { setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 460, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: 'linear-gradient(135deg,var(--purple),var(--purple2))', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: '#f0c040', fontWeight: 600 }}>🛡️ {t('clubs.join_modal_title')}</div>
            <div style={{ fontSize: 12, color: 'rgba(200,180,255,0.75)', marginTop: 2 }}>{club.emoji} {club.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: '#f0c040', marginBottom: 8 }}>{t('clubs.join_modal_sent_title')}</div>
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6 }}>{t('clubs.join_modal_sent_text')}</p>
              <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={onClose}>{t('clubs.join_modal_close')}</button>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">{t('clubs.join_modal_why')}</label>
                <textarea className="form-textarea" rows={4} placeholder={t('clubs.join_modal_placeholder')} value={msg} onChange={e => setMsg(e.target.value)} />
              </div>
              {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 12 }}>⚠️ {error}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={send} disabled={sending}>
                  {sending ? '⏳...' : `📤 ${t('clubs.join_modal_submit')}`}
                </button>
                <button className="btn btn-secondary" onClick={onClose}>{t('clubs.join_modal_cancel')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Season form ──────────────────────────────────────────
function SeasonForm({ clubId, season, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: season?.title || '',
    date: season?.date || '',
    content: season?.content || '',
    images: season?.images || '[]',
    sort_order: season?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const getImages = () => {
    try { return JSON.parse(form.images); } catch { return []; }
  };

  const handleImageFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(Array.from(files).map(uploadFile));
      const next = [...getImages(), ...urls];
      set('images', JSON.stringify(next));
    } catch { setError(t('clubs.season_image_error')); }
    setUploading(false);
  };

  const removeImage = (i) => {
    const next = getImages().filter((_, idx) => idx !== i);
    set('images', JSON.stringify(next));
  };

  const save = async () => {
    if (!form.title.trim() || !form.date) { setError(t('clubs.season_required')); return; }
    setSaving(true); setError('');
    try {
      let res;
      if (season?.id) {
        res = await api.put(`/clubs/${clubId}/seasons/${season.id}`, form);
      } else {
        res = await api.post(`/clubs/${clubId}/seasons`, form);
      }
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    } finally { setSaving(false); }
  };

  return (
    <div className="club-season-form">
      <div className="club-season-form-title">{season?.id ? `✏️ ${t('clubs.season_edit')}` : `➕ ${t('clubs.season_add')}`}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('clubs.season_title')} *</label>
          <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder={t('clubs.season_title_ph')} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('clubs.season_date')} *</label>
          <input className="form-input" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">{t('clubs.season_content')}</label>
        <WysiwygEditor value={form.content} onChange={v => set('content', v)} placeholder={t('clubs.season_content_ph')} minHeight={200} />
      </div>
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">{t('clubs.season_images')}</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {getImages().map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={url} alt="" style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
              <button onClick={() => removeImage(i)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(192,57,43,0.85)', border: 'none', color: '#fff', borderRadius: 3, width: 16, height: 16, cursor: 'pointer', fontSize: 10, lineHeight: 1, padding: 0 }}>✕</button>
            </div>
          ))}
          <button type="button" onClick={() => fileRef.current.click()}
            style={{ width: 100, height: 60, border: '2px dashed rgba(255,255,255,0.15)', borderRadius: 4, background: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {uploading ? '⏳' : '+'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={e => { handleImageFiles(e.target.files); e.target.value = ''; }} />
        </div>
      </div>
      {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? `⏳ ${t('common.loading')}` : `💾 ${t('common.save')}`}</button>
        <button className="btn btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────
export default function ClubDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { clubs: apiClubs, events: apiEvents, news: apiNews } = useData();

  const [club, setClub] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [memberStatus, setMemberStatus] = useState(null);
  const [showJoin, setShowJoin] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [clubMessages, setClubMessages] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [sidePlayers, setSidePlayers] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Sidebar calendar state
  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const calDays = useMemo(() => buildCdCal(calYear, calMonth), [calYear, calMonth]);
  const todayDay = calYear === now.getFullYear() && calMonth === now.getMonth() ? now.getDate() : -1;

  const allClubs  = apiClubs.length  ? apiClubs  : mockClubs;
  const allEvents = apiEvents.length ? apiEvents : mockEvents;
  const allNews   = apiNews.length   ? apiNews   : mockNews;

  // Sidebar data — memoized so they don't shuffle on every render
  const sideClubs = useMemo(() => shuffleArr(allClubs.filter(c => String(c.id) !== String(id))).slice(0, 5), [allClubs, id]);
  const sideLarp  = useMemo(() => shuffleArr(allEvents.filter(e => e.type === 'larp')).slice(0, 3),          [allEvents]);
  const sideNews  = useMemo(() => [...allNews].sort((a,b) => (b.created_at||'') > (a.created_at||'') ? 1 : -1).slice(0, 2), [allNews]);

  const eventDayMap = useMemo(() => {
    const map = {};
    allEvents.forEach(ev => {
      if (!ev.date) return;
      const d = new Date(ev.date);
      if (d.getFullYear() === calYear && d.getMonth() === calMonth) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(ev);
      }
    });
    return map;
  }, [allEvents, calYear, calMonth]);

  const prevMonth = () => { if (calMonth === 0) { setCalYear(y=>y-1); setCalMonth(11); } else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth === 11) { setCalYear(y=>y+1); setCalMonth(0); } else setCalMonth(m=>m+1); };

  useEffect(() => {
    const clubId = parseInt(id);
    api.get(`/clubs/${clubId}`)
      .then(r => setClub(normClub(r.data)))
      .catch(() => { const mock = mockClubs.find(c => c.id === clubId); if (mock) setClub(normClub(mock)); });
    api.get(`/reviews?entity_type=club&entity_id=${clubId}`)
      .then(r => { if (r.data.length) setUserReviews(r.data.map(normReview)); else setUserReviews(mockReviews.filter(r => r.clubId === clubId)); })
      .catch(() => setUserReviews(mockReviews.filter(r => r.clubId === clubId)));
    api.get(`/clubs/${clubId}/seasons`).then(r => setSeasons(r.data || [])).catch(() => {});
    if (user) {
      api.get(`/clubs/${clubId}/my-membership`).then(r => setMemberStatus(r.data.status)).catch(() => {});
      api.get(`/clubs/${clubId}/my-follow`).then(r => setFollowing(r.data.following)).catch(() => {});
    }
  }, [id, user]); // eslint-disable-line

  useEffect(() => {
    if (!club || !user) return;
    const canSeeMembers = isAdmin || (club.owner_id && user.id === club.owner_id);
    if (canSeeMembers) {
      api.get(`/clubs/${club.id}/members`).then(r => setMembers(r.data)).catch(() => {});
      api.get(`/clubs/${club.id}/messages`).then(r => setClubMessages(r.data)).catch(() => {});
    }
  }, [club, user, isAdmin]); // eslint-disable-line

  useEffect(() => {
    api.get('/players').then(r => {
      const arr = r.data || [];
      setSidePlayers([...arr].sort(() => Math.random() - 0.5).slice(0, 5));
    }).catch(() => {});
  }, []);

  if (!club) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('common.loading')}</div>;

  const canEdit = isAdmin || (user && club.owner_id && user.id === club.owner_id);
  const avgRating = userReviews.length
    ? (userReviews.reduce((s, r) => s + r.rating, 0) / userReviews.length).toFixed(1)
    : club.rating;

  const handleEditClick = () => { if (!user) { setShowLoginPrompt(true); return; } if (!canEdit) return; setEditMode(true); };
  const handleSave = (updated) => { setClub(normClub({ ...updated, gallery: typeof updated.gallery === 'string' ? updated.gallery : JSON.stringify(updated.gallery || []) })); setEditMode(false); };
  const handleDelete = async () => { try { await api.delete(`/clubs/${club.id}`); navigate('/clubs'); } catch (e) { setDeleteError(e.response?.data?.detail || 'Ištrynimo klaida'); setShowDeleteConfirm(false); } };
  const submitReview = () => { if (!newReview.comment.trim()) return; if (!user) { setShowLoginPrompt(true); return; } setUserReviews(r => [...r, { id: Date.now(), clubId: club.id, user: user.name || 'Jūs', rating: newReview.rating, comment: newReview.comment, date: new Date().toISOString().split('T')[0], helpful: 0 }]); setNewReview({ rating: 5, comment: '' }); };

  const handleCoverChange = async (url) => {
    const updated = { ...club, image_url: url };
    setClub(updated);
    try {
      await api.put(`/clubs/${club.id}`, {
        name: club.name, emoji: club.emoji, type: club.type, city: club.city,
        description: club.description, rules: club.rules,
        youtube_url: club.youtube || '', founded: club.founded,
        image_url: url,
        gallery: JSON.stringify(club.gallery || []),
      });
    } catch { /* silent */ }
  };

  const pendingCount = members.filter(m => m.status === 'pending').length;
  const unreadMsgCount = clubMessages.filter(m => !m.is_read).length;
  const TABS = [
    { id: 'about',    label: t('club.tab_about') },
    { id: 'reviews',  label: `${t('review.title')} (${userReviews.length})` },
    { id: 'projects', label: t('clubs.projects') },
    { id: 'rules',    label: t('clubs.rules') },
    { id: 'gallery',  label: t('clubs.gallery') },
    ...(seasons.length > 0 || canEdit ? [{ id: 'seasons', label: `📅 ${t('clubs.seasons_tab')}${seasons.length > 0 ? ` (${seasons.length})` : ''}` }] : []),
    ...(canEdit ? [
      { id: 'members',  label: `👥 ${t('clubs.members_tab')}${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
      { id: 'messages', label: `📨 ${t('club.messages_tab')}${unreadMsgCount > 0 ? ` (${unreadMsgCount})` : ''}` },
    ] : []),
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 16px' }}>
      {showLoginPrompt && <LoginPrompt onClose={() => setShowLoginPrompt(false)} />}
      {showDeleteConfirm && <ConfirmDelete name={club.name} onConfirm={handleDelete} onCancel={() => setShowDeleteConfirm(false)} />}
      {showContact && <ContactModal club={club} onClose={() => setShowContact(false)} />}
      {showJoin && <JoinRequestModal club={club} onClose={() => setShowJoin(false)} onJoined={status => { setMemberStatus(status); setShowJoin(false); }} />}

      <button className="btn btn-secondary btn-sm" style={{ margin: '16px 0' }} onClick={() => navigate(-1)}>← {t('common.back')}</button>

      {deleteError && (
        <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid #c0392b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#ff6060', fontSize: 13 }}>⚠️ {deleteError}</div>
      )}

      {/* ── 2-column: left sidebar + main content ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 24 }}>

        {/* ════════ LEFT SIDEBAR ════════ */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ── Club statistics ── */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg,var(--purple),var(--purple2))', padding: '9px 14px', fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: '#f0c040', letterSpacing: 2, textTransform: 'uppercase' }}>
              📊 {t('club.statistics')}
            </div>
            <div style={{ padding: '10px 14px' }}>
              {[
                ['👥', t('club.stat_members'),  club.members],
                ['⭐', t('club.stat_rating'),   avgRating],
                ['📅', t('club.stat_founded'),  club.founded],
                ['📍', t('club.stat_city'),     [club.city, club.country ? countryDisplay(club.country, i18n.language) : ''].filter(Boolean).join(' · ')],
                ['🎯', t('club.stat_type'),     (club.type||'').toUpperCase()],
                ['💬', t('clubs.reviews'),      userReviews.length],
              ].map(([icon, label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text3)' }}>{icon} {label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{val || '—'}</span>
                </div>
              ))}
              {club.owner_id && (
                <div style={{ paddingTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                  👑 {t('club.owner')}: <span style={{ color: 'var(--text)', fontWeight: 500 }}>
                    {user && user.id === club.owner_id ? `${user.name} (${t('club.you')})` : `#${club.owner_id}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Reklama ── */}
          <AdBox position="sidebar" />

          {/* ── Mini calendar ── */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              📅 {t('common.calendar')}
            </div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg,var(--purple),var(--purple2))', padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }} onClick={prevMonth}>‹</button>
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: '#fff', fontWeight: 600 }}>{CD_MONTH_LT[calMonth]} {calYear}</span>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }} onClick={nextMonth}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, background: 'var(--border)', padding: 1 }}>
                {['P','A','T','K','Pn','Š','S'].map(d => (
                  <div key={d} style={{ background: 'var(--bg2)', textAlign: 'center', fontSize: 9, fontWeight: 600, color: 'var(--text3)', padding: '3px 0' }}>{d}</div>
                ))}
                {calDays.map((day, i) => {
                  const hasEv = day && eventDayMap[day];
                  const isToday = day === todayDay;
                  const dateStr = day ? `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : '';
                  return (
                    <div key={i}
                      onClick={() => hasEv && navigate(`/events?date=${dateStr}`)}
                      onMouseEnter={e => { if (hasEv) e.currentTarget.style.background = 'rgba(240,192,64,0.15)'; }}
                      onMouseLeave={e => { if (hasEv) e.currentTarget.style.background = isToday ? 'rgba(240,192,64,0.1)' : 'var(--bg)'; }}
                      style={{ background: isToday ? 'rgba(240,192,64,0.1)' : 'var(--bg)', textAlign: 'center', padding: '3px 1px', fontSize: 10, cursor: hasEv ? 'pointer' : 'default', position: 'relative', minHeight: 20, color: !day ? 'transparent' : isToday ? 'var(--gold2)' : 'var(--text)', fontWeight: isToday ? 700 : 400 }}
                    >
                      {day}
                      {hasEv && <span style={{ position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)', width: 3, height: 3, borderRadius: '50%', background: 'var(--gold2)', display: 'block' }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Random 5 clubs ── */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              🏰 {t('clubs.featured')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sideClubs.map(c => (
                <div key={c.id} onClick={() => navigate(`/clubs/${c.id}`)}
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0, transition: 'border-color .15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--purple2)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div className={CD_TYPE_BG[c.type] || 'bg-club'} style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                    {c.image_url
                      ? <img src={c.image_url} alt={c.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      : c.emoji
                    }
                  </div>
                  <div style={{ padding: '6px 9px', flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>📍 {c.city}{c.country ? ` · ${countryDisplay(c.country, i18n.language)}` : ''} · {(c.type||'').toUpperCase()}</div>
                  </div>
                </div>
              ))}
              <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '6px 0', fontSize: 11 }} onClick={() => navigate('/clubs')}>
                {t('common.all_clubs')}
              </button>
            </div>
          </div>

          {/* ── Random 3 LARP renginiai ── */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              ⚔️ LARP renginiai
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {sideLarp.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>Renginių nerasta</div>
              ) : sideLarp.map(ev => {
                const color = CD_EV_COLORS[ev.type] || '#ff8080';
                const parts = (ev.date || '').split('-');
                return (
                  <div key={ev.id} onClick={() => navigate(`/events/${ev.id}`)}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', display: 'flex', gap: 0, transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = color}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{ background: color + '22', borderRight: `2px solid ${color}`, padding: '7px 7px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 38 }}>
                      <span style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 700, color, lineHeight: 1 }}>{parts[2]}</span>
                      <span style={{ fontSize: 7, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>{CD_MONTH_SHORT[parseInt(parts[1])-1]}</span>
                    </div>
                    <div style={{ padding: '7px 9px', flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>📍 {ev.city}{ev.country ? ` · ${countryDisplay(ev.country, i18n.language)}` : ''}</div>
                    </div>
                  </div>
                );
              })}
              <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '6px 0', fontSize: 11 }} onClick={() => navigate('/larp')}>
                Visi LARP →
              </button>
            </div>
          </div>

          {/* ── Paskutinės 2 naujienos ── */}
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 5, borderBottom: '1px solid var(--border)' }}>
              📰 {t('nav.news')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sideNews.map(n => (
                <div key={n.id} onClick={() => navigate(`/news/${n.id}`)}
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px', cursor: 'pointer', transition: 'border-color .15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(240,192,64,0.4)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>{n.title}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>🕒 {(n.created_at||n.date||'').slice(0,10)}</div>
                </div>
              ))}
              <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '6px 0', fontSize: 11 }} onClick={() => navigate('/news')}>
                Visos naujienos →
              </button>
            </div>
          </div>

          {/* ── Random players ── */}
          {sidePlayers.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">🧙 {t('clubs.sidebar_players')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sidePlayers.map(p => (
                  <div key={p.id} onClick={() => navigate('/players')}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', transition: 'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(240,192,64,0.4)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                      {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🧙'}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || p.nickname || 'Žaidėjas'}</div>
                      {p.class && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.class}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Antra reklama ── */}
          <AdBox position="sidebar" />

        </aside>

        {/* ════════ MAIN CONTENT ════════ */}
        <main style={{ minWidth: 0 }}>

          {/* ── Hero banner ── */}
          <div className="club-hero">
            {club.image_url && <img className="club-hero-img" src={club.image_url} alt={club.name} />}
            <div className="club-hero-overlay" />
            {canEdit && <span className="club-hero-size-hint">1980 × 280 px</span>}
            {canEdit && (
              <button className="club-hero-cover-btn" onClick={() => setActiveTab('gallery')}>
                📸 Keisti viršelį
              </button>
            )}
            <div className="club-hero-content">
              <div className="club-hero-emoji">{club.emoji}</div>
              <div className="club-hero-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                  <h1 className="club-hero-name">{club.name}</h1>
                  {club.status === 'approved' && <span className="badge badge-approved">✓ {t('common.verified')}</span>}
                  {club.status === 'pending' && <span className="badge badge-pending">⏳ {t('admin.pending')}</span>}
                </div>
                <div className="club-hero-meta">
                  <span>📍 {club.city}{club.country ? ` · ${countryDisplay(club.country, i18n.language)}` : ''}</span>
                  <span>👥 {club.members} {t('clubs.members')}</span>
                  <span style={{ color: '#ffd060' }}>⭐ {avgRating} ({userReviews.length} {t('clubs.reviews')})</span>
                  <span>📅 {club.founded}</span>
                </div>
                <div className="club-hero-tags">
                  {club.tags.map(tag => <span key={tag} className="club-hero-tag">{tag}</span>)}
                </div>
              </div>
              <div className="club-hero-actions">
                <button
                  className={following ? 'btn btn-primary' : 'btn btn-outline'}
                  style={following ? { background: 'rgba(90,62,140,0.5)', color: '#fff', borderColor: 'transparent' } : { color: '#c8b4ff', borderColor: 'rgba(200,180,255,0.4)' }}
                  onClick={async () => {
                    if (!user) { setShowLoginPrompt(true); return; }
                    setFollowLoading(true);
                    try {
                      const r = await api.post(`/clubs/${club.id}/follow`);
                      setFollowing(r.data.following);
                    } catch { /* silent */ }
                    setFollowLoading(false);
                  }}
                  disabled={followLoading}
                >
                  {following ? `❤️ ${t('clubs.following')}` : `🤍 ${t('clubs.follow')}`}
                </button>
                {user && !canEdit && (
                  memberStatus === 'approved' ? (
                    <button className="btn btn-outline" disabled style={{ color: '#60d090', borderColor: 'rgba(96,208,144,0.4)', cursor: 'default' }}>✓ {t('clubs.join_approved_status')}</button>
                  ) : memberStatus === 'pending' ? (
                    <button className="btn btn-outline"
                      style={{ color: '#ffa040', borderColor: 'rgba(255,160,64,0.4)' }}
                      title={t('clubs.join_cancel_hint')}
                      onClick={async () => {
                        if (!window.confirm(t('clubs.join_cancel_confirm'))) return;
                        await api.delete(`/clubs/${club.id}/join`);
                        setMemberStatus(null);
                      }}>⏳ {t('clubs.join_pending')}</button>
                  ) : (
                    <button className="btn btn-outline" style={{ color: '#f0c040', borderColor: 'rgba(240,192,64,0.5)' }}
                      onClick={() => setShowJoin(true)}>🛡️ {t('clubs.join_btn')}</button>
                  )
                )}
                <button className="btn btn-outline" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', opacity: user ? 1 : 0.45 }}
                  onClick={() => user && setShowContact(true)}>{t('clubs.contact')}</button>
                {(canEdit || !user) && (
                  <button className="btn btn-secondary" style={{ background: canEdit ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.07)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                    onClick={handleEditClick}>✏️ {t('clubs.edit')}</button>
                )}
                {canEdit && (
                  <button className="btn" style={{ background: 'rgba(192,57,43,0.3)', color: '#ff8080', border: '1px solid rgba(192,57,43,0.5)' }}
                    onClick={() => setShowDeleteConfirm(true)}>🗑️ {t('common.delete')}</button>
                )}
              </div>
            </div>
          </div>

          {isAdmin && <AdminAssignPanel clubId={club.id} currentOwnerId={club.owner_id} />}
          {editMode && canEdit && <EditForm club={club} onSave={handleSave} onCancel={() => setEditMode(false)} />}

          {/* ── Tabs ── */}
          <div className="club-tabs">
            {TABS.map(tab => (
              <button key={tab.id} className={`club-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
            ))}
          </div>

          {/* ── TAB: About ── */}
          {activeTab === 'about' && (
            <div>
              <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 14 }}>{t('club.description_heading')}</h3>
              {club.description
                ? <RichText text={club.description} />
                : <p style={{ color: 'var(--text3)', fontStyle: 'italic' }}>Aprašymas nepridėtas.</p>
              }
              {club.youtube && (
                <div style={{ marginTop: 24 }}>
                  <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 14 }}>▶ YouTube</h3>
                  <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    <ReactPlayer url={club.youtube} controls width="100%" height="220px" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── TAB: Reviews ── */}
          {activeTab === 'reviews' && (
            <div style={{ maxWidth: 680 }}>
              {user ? (
                <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
                  <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: 'var(--gold2)', marginBottom: 14 }}>✍️ {t('review.write')}</h3>
                  <div className="form-group">
                    <label className="form-label">{t('review.rating')}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[1,2,3,4,5].map(s => (
                        <button key={s} onClick={() => setNewReview(r => ({...r, rating: s}))}
                          style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: s <= newReview.rating ? '#ffd060' : 'var(--border2)', transition: 'all .2s' }}>★</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('review.comment')}</label>
                    <textarea className="form-textarea" value={newReview.comment} onChange={e => setNewReview(r => ({...r, comment: e.target.value}))} placeholder={t('club.review_placeholder')} />
                  </div>
                  <button className="btn btn-primary" onClick={submitReview}>📤 {t('review.submit')}</button>
                </div>
              ) : (
                <div style={{ background: 'var(--bg2)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
                  <div style={{ color: 'var(--text3)', fontSize: 14 }}>{t('club.login_for_review')}</div>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowLoginPrompt(true)}>{t('nav.login')}</button>
                </div>
              )}
              {userReviews.map(r => (
                <div key={r.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,var(--purple),var(--purple2))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🧙</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{r.user}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.date}</div>
                      </div>
                    </div>
                    <span style={{ color: '#ffd060', fontSize: 15 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</span>
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>{r.comment}</p>
                  <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                    <button style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text3)', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>👍 {r.helpful}</button>
                    <button style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>⚠ {t('review.report')}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── TAB: Projects ── */}
          {activeTab === 'projects' && (
            <div>
              <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 16 }}>📋 {t('clubs.projects')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 14 }}>
                {(club.projects || []).map((p, i) => (
                  <div key={i} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16 }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>📌</div>
                    <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{p}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{t('club.active_project')}</div>
                  </div>
                ))}
                {canEdit && (
                  <div style={{ background: 'var(--bg2)', border: '2px dashed var(--border)', borderRadius: 'var(--radius)', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text3)', fontSize: 14 }}>
                    {t('club.add_project')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB: Rules ── */}
          {activeTab === 'rules' && (
            <div style={{ maxWidth: 968 }}>
              <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 16 }}>📜 {t('clubs.rules')}</h3>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 }}>
                {club.rules
                  ? <RichText text={club.rules} />
                  : <p style={{ color: 'var(--text3)', fontStyle: 'italic' }}>{t('club.no_rules')}</p>
                }
              </div>
            </div>
          )}

          {/* ── TAB: Gallery ── */}
          {activeTab === 'gallery' && (
            <GalleryTab
              club={club}
              canEdit={canEdit}
              onGalleryChange={g => setClub(c => ({ ...c, gallery: g }))}
              onCoverChange={handleCoverChange}
            />
          )}

          {/* ── TAB: Seasons ── */}
          {activeTab === 'seasons' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', margin: 0 }}>📅 {t('clubs.seasons_tab')}</h3>
                {canEdit && !showSeasonForm && !editingSeason && (
                  <button className="btn btn-primary btn-sm" onClick={() => setShowSeasonForm(true)}>➕ {t('clubs.season_add')}</button>
                )}
              </div>

              {(showSeasonForm && !editingSeason) && (
                <SeasonForm
                  clubId={club.id}
                  onSaved={s => { setSeasons(prev => [s, ...prev]); setShowSeasonForm(false); }}
                  onCancel={() => setShowSeasonForm(false)}
                />
              )}

              {seasons.length === 0 && !showSeasonForm ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🗓️</div>
                  <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14 }}>{t('clubs.seasons_empty')}</div>
                </div>
              ) : seasons.map(s => (
                <div key={s.id}>
                  {editingSeason?.id === s.id ? (
                    <SeasonForm
                      clubId={club.id}
                      season={editingSeason}
                      onSaved={updated => { setSeasons(prev => prev.map(x => x.id === updated.id ? updated : x)); setEditingSeason(null); }}
                      onCancel={() => setEditingSeason(null)}
                    />
                  ) : (
                    <div className="club-season-card">
                      <div className="club-season-header">
                        <div>
                          <h4 className="club-season-title">{s.title}</h4>
                          {s.date && <div className="club-season-date">📅 {s.date}</div>}
                        </div>
                        {canEdit && (
                          <div className="club-season-actions">
                            <button className="btn btn-sm btn-secondary" onClick={() => setEditingSeason(s)}>✏️ {t('common.edit')}</button>
                            <button className="btn btn-sm" style={{ background: 'rgba(192,57,43,0.2)', color: '#ff8080', border: '1px solid rgba(192,57,43,0.4)' }}
                              onClick={async () => {
                                if (!window.confirm(t('clubs.season_delete_confirm'))) return;
                                await api.delete(`/clubs/${club.id}/seasons/${s.id}`);
                                setSeasons(prev => prev.filter(x => x.id !== s.id));
                              }}>🗑️</button>
                          </div>
                        )}
                      </div>
                      <div className="club-season-body">
                        {s.content && <RichText text={s.content} />}
                        {(() => {
                          try {
                            const imgs = JSON.parse(s.images || '[]');
                            if (!imgs.length) return null;
                            return (
                              <div className="club-season-images">
                                {imgs.map((url, i) => (
                                  <img key={i} src={url} alt="" className="club-season-img"
                                    onClick={() => window.open(url, '_blank')} />
                                ))}
                              </div>
                            );
                          } catch { return null; }
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── TAB: Members (owner/admin only) ── */}
          {activeTab === 'members' && canEdit && (
            <div>
              <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 16 }}>👥 {t('clubs.members_tab')}</h3>
              {members.length === 0 ? (
                <p style={{ color: 'var(--text3)', fontSize: 14 }}>{t('clubs.members_empty')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 600 }}>
                  {['pending', 'approved', 'rejected'].map(status => {
                    const group = members.filter(m => m.status === status);
                    if (!group.length) return null;
                    const label = status === 'pending'
                      ? `⏳ ${t('clubs.members_pending_label')}`
                      : status === 'approved'
                        ? `✓ ${t('clubs.members_approved_label')}`
                        : `✗ ${t('clubs.members_rejected_label')}`;
                    const labelColor = status === 'pending' ? '#ffa040' : status === 'approved' ? '#60d090' : '#ff6060';
                    return (
                      <div key={status}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: labelColor, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
                        {group.map(m => (
                          <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 6 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>{m.name || `Vartotojas #${m.user_id}`}</div>
                              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{m.email}</div>
                              {m.message && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>„{m.message}"</div>}
                              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{(m.created_at || '').slice(0, 10)}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              {status !== 'approved' && (
                                <button className="btn btn-sm" style={{ background: 'rgba(96,208,144,0.15)', color: '#60d090', border: '1px solid rgba(96,208,144,0.3)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}
                                  onClick={async () => {
                                    await api.patch(`/clubs/${club.id}/members/${m.user_id}`, { status: 'approved' });
                                    setMembers(ms => ms.map(x => x.user_id === m.user_id ? { ...x, status: 'approved' } : x));
                                    setClub(c => ({ ...c, members: c.members + (status === 'rejected' ? 0 : 1) }));
                                  }}>✓ {t('clubs.members_approve_btn')}</button>
                              )}
                              {status !== 'rejected' && (
                                <button className="btn btn-sm" style={{ background: 'rgba(255,96,96,0.1)', color: '#ff8080', border: '1px solid rgba(255,96,96,0.25)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}
                                  onClick={async () => {
                                    await api.patch(`/clubs/${club.id}/members/${m.user_id}`, { status: 'rejected' });
                                    setMembers(ms => ms.map(x => x.user_id === m.user_id ? { ...x, status: 'rejected' } : x));
                                    if (status === 'approved') setClub(c => ({ ...c, members: Math.max(0, c.members - 1) }));
                                  }}>✗ {t('clubs.members_reject_btn')}</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── TAB: Messages (owner/admin only) ── */}
          {activeTab === 'messages' && canEdit && (
            <div>
              <h3 style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)', marginBottom: 16 }}>📨 {t('club.messages_tab')}</h3>
              {clubMessages.length === 0 ? (
                <p style={{ color: 'var(--text3)', fontSize: 14 }}>{t('club.messages_empty')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 680 }}>
                  {clubMessages.map(msg => (
                    <div key={msg.id} style={{ background: msg.is_read ? 'var(--bg2)' : 'rgba(90,62,140,0.1)', border: `1px solid ${msg.is_read ? 'var(--border)' : 'rgba(90,62,140,0.4)'}`, borderRadius: 10, padding: '14px 18px', transition: 'all .15s' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            {!msg.is_read && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(90,62,140,0.4)', border: '1px solid rgba(90,62,140,0.6)', color: '#c8b4ff', letterSpacing: 1, textTransform: 'uppercase' }}>Nauja</span>}
                            <span style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 600, color: '#f0c040' }}>{msg.subject}</span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
                            {t('club.message_from')}: <span style={{ color: 'var(--text)' }}>{msg.sender_name}</span>
                            {' · '}<a href={`mailto:${msg.sender_email}`} style={{ color: 'rgba(200,180,255,0.6)', textDecoration: 'none' }}>{msg.sender_email}</a>
                            {' · '}{(msg.created_at || '').slice(0, 16).replace('T', ' ')}
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg)', borderRadius: 6, padding: '10px 12px', border: '1px solid var(--border)' }}>
                            {msg.message}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                          {!msg.is_read && (
                            <button
                              style={{ background: 'rgba(96,208,144,0.12)', color: '#60d090', border: '1px solid rgba(96,208,144,0.3)', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}
                              onClick={async () => {
                                await api.patch(`/clubs/${club.id}/messages/${msg.id}/read`);
                                setClubMessages(ms => ms.map(m => m.id === msg.id ? { ...m, is_read: 1 } : m));
                              }}>✓ {t('club.message_mark_read')}</button>
                          )}
                          <a
                            href={`mailto:${msg.sender_email}?subject=Re: ${encodeURIComponent(msg.subject)}`}
                            style={{ background: 'rgba(90,62,140,0.15)', color: '#c8b4ff', border: '1px solid rgba(90,62,140,0.35)', borderRadius: 6, padding: '5px 10px', fontSize: 11, textDecoration: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            ✉️ {t('club.message_reply')}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 32 }}><AdBox /></div>
        </main>
      </div>
    </div>
  );
}
